"use client"

import type React from "react"
import { useState, useMemo } from "react"
import { ArrowUpDown, Download, Archive, Trash2, Redo } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { FaxTableRow, SortConfig } from "@/types"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { PDFViewer } from "@/components/PdfViewer"
import { cn, digStr } from "@/lib/utils"
import { Link, usePage } from "@inertiajs/react"
import DocumentClassifierSelect from "./DocumentClassifierSelect"
import { Pagy } from "./Pagy"

interface FaxTableProps {
  faxes: FaxTableRow[]
  isMobile: boolean
  showFromColumn: boolean
  showActions?: boolean
  onArchive?: (faxId: number) => void
  onDelete?: (faxId: number) => void
}

type PagyProps = {
  page: number
  last: number
  previous: number | null
  next: number | null
}

export function FaxTable({
  faxes,
  showFromColumn: isInbound,
  showActions = true,
  onArchive,
  onDelete,
}: FaxTableProps) {
  const [showPDF, setShowPDF] = useState(false)
  const [pdf_id, setPdfId] = useState<number | null>(null)

  const { props } = usePage<{ pagy: PagyProps }>()
  const { pagy } = props
  console.log("pagy:", pagy)

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "created_at",
    direction: "desc",
  })

  function reprocessFax(id: number) {
    fetch(`/faxes/${id}/reprocess`)
  }

  function openPDF(id: number, e?: React.MouseEvent) {
    e?.stopPropagation()
    e?.preventDefault()
    setPdfId(id)
    setShowPDF(true)
  }

  const handleSort = (key: keyof FaxTableRow) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }))
  }

  const sortedFaxes = useMemo(() => {
    return [...faxes].sort((a, b) => {
      const av = a[sortConfig.key]
      const bv = b[sortConfig.key]
      if (av == null || bv == null) return 0
      if (typeof av === "string" && typeof bv === "string") {
        return sortConfig.direction === "asc"
          ? av.localeCompare(bv)
          : bv.localeCompare(av)
      }
      if (typeof av === "number" && typeof bv === "number") {
        return sortConfig.direction === "asc" ? av - bv : bv - av
      }
      return 0
    })
  }, [faxes, sortConfig])

  const SortButton = ({
    column,
    children,
  }: {
    column: keyof FaxTableRow
    children: React.ReactNode
  }) => (
    <button
      onClick={() => handleSort(column)}
      className="flex items-center gap-1 hover:text-foreground text-xs"
    >
      {children}
      <ArrowUpDown
        className={cn(
          "h-3.5 w-3.5",
          sortConfig.key === column
            ? "text-primary"
            : "text-muted-foreground",
        )}
      />
    </button>
  )

  const formatTimestamp = (iso?: string | null) => {
    if (!iso) return ""
    const d = new Date(iso)
    const now = new Date()
    return d.toDateString() === now.toDateString()
      ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      : d.toLocaleDateString([], {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })
  }

  console.log("Rendering FaxTable with faxes:", sortedFaxes)
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-4 px-3 py-2 border-b text-xs text-muted-foreground">
        <div className="w-[140px] md:w-[180px]">
          <SortButton column={isInbound ? "from" : "to"}>
            {isInbound ? "From" : "To"}
          </SortButton>
        </div>

        <div className="min-w-[120px] md:min-w-[180px]">
          <SortButton
            column={isInbound ? "classification" : "status"}
          >
            {isInbound ? "Classification" : "Status"}
          </SortButton>
        </div>

        <div className="flex-1">
          <SortButton column="summary">Summary</SortButton>
        </div>

        <div className="w-[80px] md:w-[120px]">
          <SortButton column="page_count">Pages</SortButton>
        </div>

        <div className="w-[120px] text-right">Date/Time</div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-border">
        {sortedFaxes.length === 0 ? (
          <div className="h-24 flex items-center justify-center text-sm text-muted-foreground">
            No faxes found
          </div>
        ) : (
          sortedFaxes?.map((fax) => (
            <Link
              key={fax.id}
              href={`/faxes/${fax.id}`}
              preserveScroll
              className="group block"
            >
              <div className="relative flex items-center gap-3 px-3 py-2 hover:bg-accent/5 min-h-[48px]">
                <div className="w-[140px] md:w-[180px] font-semibold truncate">
                  {isInbound ? fax?.from : fax?.to}
                </div>

                <div className="min-w-[120px] md:min-w-[180px] truncate text-muted-foreground">
                  <DocumentClassifierSelect
                    fax_classifier={fax?.classification}
                  />
                </div>

                <div className="flex-1 truncate text-muted-foreground">
                  {digStr(fax, "summary", "summary") || "No summmary"}
                </div>

                <div className="w-[80px] md:w-[120px] text-muted-foreground">
                  {fax?.page_count}
                </div>

                <div className="w-[120px] text-right text-xs text-muted-foreground">
                  {formatTimestamp(fax?.created_at)}
                </div>

                {/* Actions */}
                <div
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-card  opacity-0 group-hover:opacity-100 flex gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => openPDF(fax.id, e)}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault()
                      reprocessFax(fax.id)
                    }}
                  >
                    <Redo className="h-3.5 w-3.5" />
                  </Button>

                  {showActions && onArchive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault()
                        onArchive(fax.id)
                      }}
                    >
                      <Archive className="h-3.5 w-3.5" />
                    </Button>
                  )}

                  {showActions && onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault()
                        onDelete(fax.id)
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </Link>
          ))
        )}
      </div>

     <Pagy pagy={pagy} />  

      {/* PDF Viewer */}
      <Sheet open={showPDF} onOpenChange={setShowPDF}>
        <SheetContent
          side="right"
          className="

             z-50
  bg-background
  shadow-lg
  transition ease-in-out
  data-[state=closed]:duration-300
  data-[state=open]:duration-500
  data-[state=open]:animate-in
  data-[state=closed]:animate-out
  data-[state=closed]:fade-out
  data-[state=open]:fade-in
  h-screen w-96
  max-w-none
  p-0

          "
        >
          <SheetHeader className="border-b w-full px-4 py-3">
            <SheetTitle>PDF Viewer</SheetTitle>
          </SheetHeader>
          <PDFViewer pdfUrl={`${pdf_id}`} fileName="record.pdf" />
        </SheetContent>
      </Sheet>
    </div>
  )
}
